import { LabelWithDescription } from '@/components/LabelWithdescription';
import {
	AccessAlarmsOutlined,
	DialpadOutlined,
	HourglassBottomOutlined,
	RadioButtonCheckedOutlined,
	VoicemailOutlined,
	WebhookOutlined,
	PinOutlined,
	InfoOutlined,
	WarningAmberOutlined,
	Block,
	SmsFailedOutlined,
	FollowTheSignsOutlined,
	NaturePeopleOutlined,
	SmartToyOutlined,
	InterpreterModeOutlined,
} from '@mui/icons-material';
import {
	Button,
	Col,
	Empty,
	Form,
	Input,
	notification,
	Row,
	Slider,
	Spin,
	Switch,
	Tooltip,
	Select,
	Alert,
} from 'antd';
import './TweakAdvancedSettingsForm.scss';
import { useState } from 'react';
import { CustomModal } from '@/components/CustomModal';
import ExtensionDigitBuilderForm from './ExtensionDigitBuilderForm/ExtensionDigitBuilderForm';
import ZapierIntegrationForm from './ZapierIntegration/ZapierIntegrationForm/ZapierIntegrationForm';
import MaxUsageLimitForm from './MaxUsageLimit/MaxUsageLimitForm';
import { OverViewCardForm } from '../OverviewCardForm';
import { QueryObserverResult, RefetchOptions, useMutation } from '@tanstack/react-query';
import { updateUser } from '@/api/user';
import { TweakFormInitialValues } from '@/types/TTweakForm';
import { DeleteOutlined, LoadingOutlined, RobotOutlined } from '@ant-design/icons';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { closeModal, openModal } from '@/store/modal/slice';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgentId, getUserFeatures } from '@/store/account/selector';
import { MODAL_IDS } from 'src/enums/EModal';
import PhoneNumberInput from '@/components/Inputs/PhoneNumberInput';
import { User } from '@/types/TAccounts';
import { handlePrefixPlusOnPhone } from '@/utils/helper';
import LearnMore from '@/components/Buttons/LearnMore';
import { TUTORIALS } from 'src/enums/ETutorials';
import BlockedNumbersForm from './BlockedNumbers/BlockedNumbersForm';
import CopyableText from '@/components/LabelWithdescription/CopyableText';
import { useFeatureFlag } from 'src/Hooks/useFeatureFlag';
import { FeatureFlag } from 'src/enums/EFeatureFlags';
import { EAIModels, EAIModelsData } from 'src/enums/EAIModels';
import { EAgentSettings } from 'src/enums/EAgentSettings';

const TweakAdvancedSettingsForm: React.FC<{
	refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>;
	formInitialValues: TweakFormInitialValues;
	activeField: string | null;
	debrand: boolean;
	isNoviUser: boolean;
}> = ({ refetch, formInitialValues, activeField, debrand, isNoviUser }) => {
	const dispatch = useAppDispatch();
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const [form] = Form.useForm<{
		chattiness: number;
		endpointing: number;
		interruption_words: number;
		maxCallDuration: number;
		extension_digits: {
			extension: number;
			phoneNumber: number;
		};
		voicemails_enabled: boolean;
		recordVoicemailAfterNSec: number;
		call_recording_enabled: boolean | string;
		recordTransferredCall: boolean;
		call_memory_enabled?: boolean;
		interruptionSensitivity: number;
		word_pronunciation: {
			pronunciation: string;
			word: string;
		}[];
		time_zone: any;
		advancedTimeControls: {
			start_time: string;
			end_time: string;
			phone_number: number;
		}[];
		autoHangupAfterInteraction: boolean;
		autoHangupAfterInteractionText?: string;
		idleMessage?: string;
		autoHangupIdleMessage?: string;
		autoHangupOnSilence: number;
		disableReceptionistSettings?: {
			disabled?: boolean;
			disableType: string; // enum  --> ["message", "forward"]
			forwardingNumber?: string;
			hangupMessage?: string;
		};
		blockedNumbers: string[];
		promptForLandline: string;
		roboCallWhitelistNumbers: string[];
		roboCallBlocklistNumbers: string[];
		llmTemperature: number;
		enabledRoboCallDetection: boolean;
		aiModel: string;
		aiPrompt: string;
		allowSmsReplyInput: boolean;
		roboSMSBlocklistNumbers: {
			number: string;
			time: number;
		}[];
		backgroundAudioEnabled: boolean;
		backgroundRoomAmbienceSound: number;
		backgroundKeyboardSound: number;
		rateCallSurveyEnabled: boolean;
	}>();

	const features = useAppSelector(getUserFeatures);
	const isIdleMessagesForInactivityEnabled = useFeatureFlag(
		FeatureFlag.IDLE_MESSAGES_FOR_INACTIVITY,
	);
	const [isFieldTouchReset, setIsFieldTouchReset] = useState<boolean>(true);

	const roboSMSBlocklistNumbersCurrent = formInitialValues?.roboSMSBlocklistNumbers
		? formInitialValues.roboSMSBlocklistNumbers.filter((item) => item.time > Date.now())
		: [];
	const roboSMSBlocklistNumbersPrevious = formInitialValues?.roboSMSBlocklistNumbers
		? formInitialValues.roboSMSBlocklistNumbers.filter((item) => item.time < Date.now())
		: [];

	const mutation = useMutation({
		mutationKey: ['update-client'],
		mutationFn: updateUser,
		onSuccess: () => {
			refetch();
			notification.success({
				key: 'form-success',
				message: 'Details has been saved successfully!',
				duration: 3,
			});
			setIsFieldTouchReset(false);
			dispatch(closeModal());
		},
	});

	const validateForm = () => {
		const isDisabled = form.getFieldValue(['disableReceptionistSettings', 'disabled']);
		const disableType = form.getFieldValue(['disableReceptionistSettings', 'disableType']);

		if (isDisabled && !disableType) {
			notification.error({
				message: 'Validation Error',
				description: 'Please select how to handle incoming calls when receptionist is disabled.',
			});
			return false;
		}
		if (isDisabled && disableType === 'forward') {
			const forwardingNumber = form.getFieldValue([
				'disableReceptionistSettings',
				'forwardingNumber',
			]);
			if (!forwardingNumber) {
				notification.error({
					message: 'Validation Error',
					description: 'Please enter a forwarding number.',
				});
				return false;
			}
		}
		if (isDisabled && disableType === 'message') {
			const hangupMessage = form.getFieldValue(['disableReceptionistSettings', 'hangupMessage']);
			if (!hangupMessage) {
				notification.error({
					message: 'Validation Error',
					description: 'Please enter a hangup message.',
				});
				return false;
			}
		}

		return true;
	};

	const handleSubmit = async (value: TweakFormInitialValues) => {
		if (!validateForm()) {
			return;
		}

		const mainDisableReceptionistSettings = form.getFieldValue('disableReceptionistSettings');
		if (!value.disableReceptionistSettings?.disabled) {
			form.setFieldsValue({
				disableReceptionistSettings: {
					disabled: false,
					disableType: '',
					forwardingNumber: '',
					hangupMessage: '',
				},
			});
		}
		await mutation.mutateAsync({
			maxCallDuration: value.maxCallDuration,
			voicemails_enabled: value.voicemails_enabled,
			recordVoicemailAfterNSec: value.recordVoicemailAfterNSec,
			call_recording_enabled: value.call_recording_enabled,
			recordTransferredCall: value.recordTransferredCall,
			call_memory_enabled: value.call_memory_enabled,
			autoHangupAfterInteraction: value.autoHangupAfterInteraction,
			autoHangupAfterInteractionText: value.autoHangupAfterInteractionText,
			idleMessage: value.idleMessage,
			autoHangupIdleMessage: value.autoHangupIdleMessage,
			autoHangupOnSilence: value.autoHangupOnSilence,
			disableReceptionistSettings: value.disableReceptionistSettings?.disabled
				? {
						disabled: value.disableReceptionistSettings?.disabled,
						disableType:
							value.disableReceptionistSettings?.disableType ||
							mainDisableReceptionistSettings?.disableType,
						forwardingNumber: handlePrefixPlusOnPhone(
							value.disableReceptionistSettings?.forwardingNumber || '',
						),
						hangupMessage: value.disableReceptionistSettings?.hangupMessage,
					}
				: {
						disabled: false,
					},
			promptForLandline: value.promptForLandline,
			llmTemperature: value.llmTemperature,
			enabledRoboCallDetection: value.enabledRoboCallDetection,
			aiModel: value.aiModel,
			aiPrompt: value.aiPrompt,
			allowSmsReplyInput: value.allowSmsReplyInput,
			backgroundAudioEnabled: value.backgroundAudioEnabled,
			backgroundRoomAmbienceSound: value.backgroundRoomAmbienceSound,
			backgroundKeyboardSound: value.backgroundKeyboardSound,
			rateCallSurveyEnabled: value.rateCallSurveyEnabled,
			agentId: selectedAgentId as string,
		});
		dispatch(closeModal());
	};

	const handleExtensionDigitsClick = () => {
		dispatch(openModal({ modalId: MODAL_IDS.EXTENSION_DIGIT_MODAL }));
	};

	const handleZapierIntegrationClick = () => {
		if (!isNoviUser) {
			if (!features?.advancedCapabilities?.zapierIntegration?.value) {
				dispatch(openModal({ modalId: MODAL_IDS.SUBSCRIPTION_MODAL }));
				return;
			}
		}

		dispatch(openModal({ modalId: MODAL_IDS.ZAPIER_INTEGRATION_MODAL }));
	};

	const handleMaxUsageLimitClick = () => {
		dispatch(openModal({ modalId: MODAL_IDS.MAX_USAGE_LIMIT_MODAL }));
	};

	const handleBlockedNumbersClick = () => {
		dispatch(openModal({ modalId: MODAL_IDS.BLOCKED_NUMBERS_MODAL }));
	};

	const handleConfirm = () => {
		dispatch(closeModal());
	};
	const handleClose = () => {
		dispatch(closeModal());
		window.dispatchEvent(new Event('modalClosed'));
	};

	const handleMaxCallDurationChange = (value: number) => {
		if (!features?.advancedCapabilities?.setMaxRecipientMinutes?.value) {
			return;
		}
		form.setFieldsValue({ maxCallDuration: value });
	};

	const handleVoicemailAfterNDurationChange = (value: number) => {
		form.setFieldsValue({ recordVoicemailAfterNSec: value });
	};

	const handleMaxSilenceDuration = (value: number) => {
		if (!features?.advancedCapabilities?.setMaxSilenceDuration?.value) {
			return;
		}
		form.setFieldsValue({ autoHangupOnSilence: value });
	};

	const moveToRobocallWhitelist = async (value: string) => {
		formInitialValues.roboCallWhitelistNumbers?.push(value);
		const bl = formInitialValues.roboCallBlocklistNumbers?.filter((item) => item !== value);
		await mutation.mutateAsync({
			roboCallWhitelistNumbers: JSON.stringify(formInitialValues.roboCallWhitelistNumbers || []),
			roboCallBlocklistNumbers: JSON.stringify(bl || []),
		});
	};

	const moveToRobocallBlocklist = async (value: string) => {
		formInitialValues.roboCallBlocklistNumbers?.push(value);
		const wl = formInitialValues.roboCallWhitelistNumbers?.filter((item) => item !== value);
		await mutation.mutateAsync({
			roboCallWhitelistNumbers: JSON.stringify(wl || []),
			roboCallBlocklistNumbers: JSON.stringify(formInitialValues.roboCallBlocklistNumbers || []),
		});
	};

	const removeRoboSMSBlacklist = async (value: string) => {
		const bl = formInitialValues?.roboSMSBlocklistNumbers?.filter((item) => item.number !== value);
		await mutation.mutateAsync({
			roboSMSBlocklistNumbers: JSON.stringify(bl || []),
		});
	};

	const FormItems = () => (
		<>
			{activeField === 'aiModel' || activeField === null ? (
				<Form.Item
					className={
						activeField === 'aiModel' ? `${baseClass}_active-class` : `${baseClass}__item-container`
					}
				>
					<div className={`${baseClass}__item`}>
						<div className={`${baseClass}__details-container flex flex-col gap-6`}>
							<div className="flex flex-col">
								<LabelWithDescription
									customClassName="w-8/12"
									label="AI Model"
									description="Select which AI model powers your receptionist"
									icon={<SmartToyOutlined className="mr-1 mb-1" />}
									showDescriptionDirectly={true}
								/>
								<Form.Item name="aiModel" className="mb-0">
									<Select className="w-full" defaultValue={EAIModels.openai_gpt4o}>
										{EAIModelsData.map((item, index) => (
											<Select.Option key={index} value={item.value}>
												{item.label}
											</Select.Option>
										))}
									</Select>
								</Form.Item>
							</div>
						</div>
					</div>
				</Form.Item>
			) : (
				''
			)}

			{activeField === 'extensionDigits' || activeField === null ? (
				<Form.Item
					className={
						activeField === 'extensionDigits'
							? `${baseClass}_active-class`
							: `${baseClass}__item-container`
					}
					valuePropName="checked"
					label={
						<div className={`${baseClass}__item`}>
							<LabelWithDescription
								label="Extension Digits"
								description='You can set up extension codes that your AI Receptionist can handle to reroute the caller. i.e. "press 3 for billing department".'
								icon={<DialpadOutlined className="mr-1 mb-1" />}
								showDescriptionDirectly={true}
							/>
							<CustomModal
								title={
									<>
										<PinOutlined className="!text-4xl" /> <strong>Extension Digits Builder</strong>
									</>
								}
								description="You can set up extension codes that your AI Receptionist can handle to reroute the caller. i.e. 'press 3 for billing department'."
								children={
									<ExtensionDigitBuilderForm
										extension_digits={formInitialValues.extension_digits || []}
										refetch={refetch}
										debrand={debrand}
									/>
								}
								confirmAction={handleConfirm}
								cancelAction={handleClose}
								footer={null}
								modalId={MODAL_IDS.EXTENSION_DIGIT_MODAL}
								width={600}
							/>
						</div>
					}
				>
					<div className="flex justify-end mr-2">
						<Button type="primary" onClick={handleExtensionDigitsClick}>
							View Details
						</Button>
					</div>
				</Form.Item>
			) : (
				''
			)}

			{activeField === 'zapierIntegration' || activeField === null ? (
				<Form.Item
					className={
						activeField === 'zapierIntegration'
							? `${baseClass}_active-class`
							: `${baseClass}__item-container`
					}
					valuePropName="checked"
					label={
						<div className={`${baseClass}__item`}>
							<LabelWithDescription
								customClassName="w-full"
								label="Zapier Integration"
								description="Your AI Receptionist can connect to other apps."
								icon={<WebhookOutlined className="mr-1 mb-1" />}
								showDescriptionDirectly={true}
							/>
							{!debrand && (
								<div className="mt-4">
									<LearnMore tutorialId={TUTORIALS.ZAPIER_INTEGRATION} debrand={debrand} />
								</div>
							)}
							<CustomModal
								title={<strong>Connect With Zapier</strong>}
								description="Your AI Receptionist can connect to other apps."
								children={<ZapierIntegrationForm debrand={debrand} enabled={true} />}
								confirmAction={handleConfirm}
								footer={null}
								modalId={MODAL_IDS.ZAPIER_INTEGRATION_MODAL}
							/>
						</div>
					}
				>
					<Button
						className={`${baseClass}__item-container_switch`}
						type="primary"
						onClick={handleZapierIntegrationClick}
					>
						View Details
					</Button>
				</Form.Item>
			) : (
				''
			)}

			{activeField === 'allowVoicemails' || activeField === null ? (
				<Form.Item
					className={
						activeField === 'allowVoicemails'
							? `${baseClass}_active-class`
							: `${baseClass}__item-container`
					}
				>
					<div className={`${baseClass}__item`}>
						<div className={`${baseClass}__details-container flex flex-col gap-6`}>
							<div className="flex items-start justify-between">
								<LabelWithDescription
									customClassName="w-8/12"
									label="Allow Voicemails"
									description='If someone asks to leave a message or voicemail, the AI Receptionist will collect a voicemail. This message will be separately shown in the "Voicemails" section of the dashboard.'
									icon={<VoicemailOutlined className="mr-1 mb-1" />}
									showDescriptionDirectly={true}
								/>
								<Form.Item name={'voicemails_enabled'} valuePropName="checked">
									<Switch size="small" />
								</Form.Item>
							</div>

							<Form.Item
								noStyle
								shouldUpdate={(prevValues, currentValues) =>
									prevValues.voicemails_enabled !== currentValues.voicemails_enabled
								}
							>
								{({ getFieldValue }) => {
									const isEnabled = getFieldValue('voicemails_enabled');

									return (
										isEnabled && (
											<>
												<Form.Item
													className={`${baseClass}__item-container`}
													name="recordVoicemailAfterNSec"
													label={
														<LabelWithDescription
															customClassName="w-8/12"
															label="Record Voicemail After Silence (N Seconds)"
															description="Specify the number of seconds of silence after the caller finishes speaking, after which the system will assume the voicemail recording is complete."
															icon={<AccessAlarmsOutlined className="mr-1 mb-1" />}
															showDescriptionDirectly={true}
														/>
													}
												>
													<Slider
														min={1}
														max={10}
														className="mx-4"
														value={form.getFieldValue('recordVoicemailAfterNSec')}
														onChange={handleVoicemailAfterNDurationChange}
													/>
												</Form.Item>
											</>
										)
									);
								}}
							</Form.Item>
						</div>
					</div>
				</Form.Item>
			) : (
				''
			)}

			{activeField === 'callRecording' || activeField === null ? (
				<Form.Item
					className={
						activeField === 'callRecording'
							? `${baseClass}_active-class`
							: `${baseClass}__item-container`
					}
					name="call_recording_enabled"
					valuePropName="checked"
					label={
						<div className={`${baseClass}__item`}>
							<LabelWithDescription
								customClassName="w-8/12"
								label="Enable Call Recording"
								description="Record all calls. Recorded calls will be stored in the dashboard for review."
								icon={<RadioButtonCheckedOutlined className="mr-1 mb-1" />}
								showDescriptionDirectly={true}
							/>
							{!debrand && (
								<div className="mt-4">
									<LearnMore tutorialId={TUTORIALS.CALL_RECORDING} debrand={debrand} />
								</div>
							)}
						</div>
					}
				>
					<Switch className={`${baseClass}__item-container_switch`} size="small" />
				</Form.Item>
			) : (
				''
			)}
			{activeField === 'recordTransferredCall' || activeField === null ? (
				<Form.Item
					noStyle
					shouldUpdate={(prevValues, currentValues) =>
						prevValues.call_recording_enabled !== currentValues.call_recording_enabled
					}
				>
					{({ getFieldValue }) => {
						const isCallRecordingEnabled = getFieldValue('call_recording_enabled');
						return (
							<Form.Item
								className={
									activeField === 'recordTransferredCall'
										? `${baseClass}_active-class`
										: `${baseClass}__item-container`
								}
								name="recordTransferredCall"
								valuePropName="checked"
								label={
									<div className={`${baseClass}__item`}>
										<LabelWithDescription
											customClassName="w-8/12"
											label="Enable Transferred Call Recording"
											description="Record all transferred calls. Recorded calls will be stored in the dashboard for review."
											icon={<RadioButtonCheckedOutlined className="mr-1 mb-1" />}
											showDescriptionDirectly={true}
										/>
										{!debrand && (
											<div className="mt-4">
												<LearnMore tutorialId={TUTORIALS.CALL_RECORDING} debrand={debrand} />
											</div>
										)}
									</div>
								}
							>
								<Switch
									className={`${baseClass}__item-container_switch`}
									size="small"
									disabled={!isCallRecordingEnabled}
									title={!isCallRecordingEnabled ? 'Enable Call Recording first' : undefined}
								/>
							</Form.Item>
						);
					}}
				</Form.Item>
			) : (
				''
			)}

			{activeField === 'backgroundAudioEnabled' || activeField === null ? (
				<Form.Item
					className={
						activeField === 'backgroundAudioEnabled'
							? `${baseClass}_active-class`
							: `${baseClass}__item-container`
					}
				>
					<div className={`${baseClass}__item`}>
						<div className={`${baseClass}__details-container flex flex-col gap-6`}>
							<div className="flex items-start justify-between">
								<LabelWithDescription
									customClassName="w-8/12"
									label="Ambient Background Noise"
									description="Enable ambient background noise—such as room sounds or light keyboard typing—to help the caller sense a live receptionist is handling the call."
									icon={<InterpreterModeOutlined className="mr-1 mb-1" />}
									showDescriptionDirectly={true}
								/>
								<Form.Item name={'backgroundAudioEnabled'} valuePropName="checked">
									<Switch size="small" />
								</Form.Item>
							</div>

							<Form.Item
								noStyle
								shouldUpdate={(prevValues, currentValues) =>
									prevValues.backgroundAudioEnabled !== currentValues.backgroundAudioEnabled
								}
							>
								{({ getFieldValue }) => {
									const isEnabled = getFieldValue('backgroundAudioEnabled');

									return (
										isEnabled && (
											<>
												<Form.Item
													name="backgroundRoomAmbienceSound"
													label={<LabelWithDescription label="Background Room Ambience Sound" />}
												>
													<Slider
														min={0}
														max={1}
														step={0.1}
														marks={{
															0: {
																label: <span className="text-nowrap">0</span>,
															},
															1: {
																label: <span className="text-nowrap">1</span>,
															},
														}}
														className="mx-4"
														value={form.getFieldValue('backgroundRoomAmbienceSound')}
														onChange={(value) =>
															form.setFieldsValue({ backgroundRoomAmbienceSound: value })
														}
													/>
												</Form.Item>
												<Form.Item
													name="backgroundKeyboardSound"
													label={<LabelWithDescription label="Background Keyboard Typing Sound" />}
												>
													<Slider
														min={0}
														max={1}
														step={0.1}
														marks={{
															0: {
																label: <span className="text-nowrap">0</span>,
															},
															1: {
																label: <span className="text-nowrap">1</span>,
															},
														}}
														className="mx-4"
														value={form.getFieldValue('backgroundKeyboardSound')}
														onChange={(value) =>
															form.setFieldsValue({ backgroundKeyboardSound: value })
														}
													/>
												</Form.Item>
											</>
										)
									);
								}}
							</Form.Item>
						</div>
					</div>
				</Form.Item>
			) : (
				''
			)}

			{activeField === EAgentSettings.TOGGLE_RATE_CALL_SURVEY || activeField === null ? (
				<Form.Item
					className={
						activeField === EAgentSettings.TOGGLE_RATE_CALL_SURVEY
							? `${baseClass}_active-class`
							: `${baseClass}__item-container`
					}
				>
					<div className={`${baseClass}__item`}>
						<div className={`${baseClass}__details-container flex flex-col gap-6`}>
							<div className="flex items-start justify-between">
								<LabelWithDescription
									customClassName="w-8/12"
									label="Toggle Rate the Call Survey"
									description="This setting lets you turn on or off the 'Rate the Call' survey. When enabled, users will be asked to rate their call experience right after a conversation ends."
									showDescriptionDirectly={true}
								/>
								<Form.Item name={'rateCallSurveyEnabled'} valuePropName="checked">
									<Switch size="default" />
								</Form.Item>
							</div>
						</div>
					</div>
				</Form.Item>
			) : null}

			{activeField === 'allowSmsReplyInput' || activeField === null ? (
				<Form.Item
					className={
						activeField === 'allowSmsReplyInput'
							? `${baseClass}_active-class`
							: `${baseClass}__item-container`
					}
					name="allowSmsReplyInput"
					valuePropName="checked"
					label={
						<div className={`${baseClass}__item`}>
							<LabelWithDescription
								customClassName="w-8/12"
								label="SMS Fallback for Voice Recognition"
								description="When the AI has trouble understanding voice input, it will ask the caller to send information via text message. This helps improve communication when voice recognition is challenging."
								icon={<SmsFailedOutlined className="mr-1 mb-1" />}
								showDescriptionDirectly={true}
							/>
						</div>
					}
				>
					<Switch
						className={`${baseClass}__item-container_switch`}
						size="small"
						defaultChecked={true}
					/>
				</Form.Item>
			) : (
				''
			)}

			{activeField === 'temperoryDisabled' || activeField === null ? (
				<Form.Item
					className={
						activeField === 'temperoryDisabled'
							? `${baseClass}_active-class`
							: `${baseClass}__item-container`
					}
				>
					<div className={`${baseClass}__item`}>
						<div className={`${baseClass}__details-container flex flex-col gap-6`}>
							<div className="flex items-center justify-between">
								<LabelWithDescription
									customClassName="w-8/12"
									label="Temporary Disable"
									description="Temporarily disable your Receptionist by either setting a default hangup message or specifying a number to automatically forward calls to."
									icon={<WarningAmberOutlined fontSize="large" className="mr-1 mb-1" />}
									showDescriptionDirectly={true}
								/>
								<Form.Item name={['disableReceptionistSettings', 'disabled']}>
									<Switch size="small" />
								</Form.Item>
							</div>

							<Form.Item
								noStyle
								shouldUpdate={(prevValues, currentValues) =>
									prevValues.disableReceptionistSettings?.disabled !==
										currentValues.disableReceptionistSettings?.disabled ||
									prevValues.disableReceptionistSettings?.disableType !==
										currentValues.disableReceptionistSettings?.disableType
								}
							>
								{({ getFieldValue, setFieldsValue }) => {
									const isDisabled = getFieldValue(['disableReceptionistSettings', 'disabled']);
									const disableType = getFieldValue(['disableReceptionistSettings', 'disableType']);

									const handleSwitchChange = (type: string) => {
										setFieldsValue({
											disableReceptionistSettings: {
												...getFieldValue('disableReceptionistSettings'),
												disableType: type,
											},
										});
									};

									return (
										isDisabled && (
											<>
												<div className="flex items-center justify-center gap-2 py-1 px-2 text-sm bg-yellow-100 my-2 border rounded-md">
													<InfoOutlined fontSize="small" className="text-blue-600" />
													<span>
														Your receptionist will be disabled. Please select from below, what will
														happen if your customer calls this receptionist number
													</span>
												</div>
												<div className="flex flex-col gap-2">
													<div className={`${baseClass}__details`}>
														<span>Forward all incoming calls to another number</span>
														<Switch
															size="small"
															checked={disableType === 'forward'}
															onChange={(checked) => handleSwitchChange(checked ? 'forward' : '')}
														/>
													</div>
													{disableType === 'forward' && (
														<div className="ml-[20px] sm:ml-[45px]">
															<PhoneNumberInput
																name={['disableReceptionistSettings', 'forwardingNumber']}
															/>
														</div>
													)}
												</div>
												<div className="flex flex-col gap-2">
													<div className={`${baseClass}__details`}>
														<span>Say a custom message and automatically hang up</span>
														<Switch
															size="small"
															checked={disableType === 'message'}
															onChange={(checked) => handleSwitchChange(checked ? 'message' : '')}
														/>
													</div>
													{disableType === 'message' && (
														<div className="ml-[20px] sm:ml-[45px]">
															<Form.Item name={['disableReceptionistSettings', 'hangupMessage']}>
																<Input />
															</Form.Item>
														</div>
													)}
												</div>
											</>
										)
									);
								}}
							</Form.Item>
						</div>
					</div>
				</Form.Item>
			) : (
				''
			)}

			{activeField === 'maxUsageLimit' || activeField === null ? (
				<Form.Item
					className={
						activeField === 'maxUsageLimit'
							? `${baseClass}_active-class`
							: `${baseClass}__item-container`
					}
					label={
						<div className={`${baseClass}__item`}>
							<LabelWithDescription
								customClassName="w-8/12"
								label="Max Usage Limit"
								description="Maximum usage limit (in minutes) for your AI receptionist. Once your usage reaches 50%, 75% or 100% of the limit you will receive an email notification."
								icon={<HourglassBottomOutlined className="mr-1 mb-1" />}
								showDescriptionDirectly={true}
							/>

							<CustomModal
								title={<strong>Set Maximum Usage Limit</strong>}
								description="Maximum usage limit (in minutes) for your AI receptionist. Once your usage reaches 50%, 75% or 100% of the limit you will receive an email notification."
								children={
									<MaxUsageLimitForm
										refetch={refetch}
										usage_notification_emails={formInitialValues.usage_notification_emails}
										max_minutes={formInitialValues.max_minutes}
										minutesLimitEnabled={formInitialValues.minutesLimitEnabled}
									/>
								}
								modalId={MODAL_IDS.MAX_USAGE_LIMIT_MODAL}
								confirmAction={handleConfirm}
								footer={null}
							/>
						</div>
					}
				>
					<div className="flex justify-end">
						<Button type="primary" onClick={handleMaxUsageLimitClick}>
							View Details
						</Button>
					</div>
				</Form.Item>
			) : (
				''
			)}

			{activeField === 'maxCallDuration' || activeField === null ? (
				<Form.Item
					className={
						activeField === 'maxCallDuration'
							? `${baseClass}_active-class`
							: `${baseClass}__item-container`
					}
					name="maxCallDuration"
					label={
						<div className={`${baseClass}__item`}>
							<LabelWithDescription
								customClassName="w-8/12"
								label="Maximum Call Duration"
								description="Maximum call duration (in minutes) of the AI receptionist before it automatically hang up"
								icon={<AccessAlarmsOutlined className="mr-1 mb-1" />}
								showDescriptionDirectly={true}
							/>
							{!debrand && (
								<div className="mt-4">
									<LearnMore tutorialId={TUTORIALS.MAX_CALL_DURATION} debrand={debrand} />
								</div>
							)}
						</div>
					}
				>
					<Slider
						min={2}
						max={30}
						className="mx-4"
						value={form.getFieldValue('maxCallDuration')}
						onChange={handleMaxCallDurationChange}
					/>
				</Form.Item>
			) : (
				''
			)}

			{activeField === 'hangupWorkflow' || activeField === null ? (
				<div>
					<Form.Item
						label={
							<div className="flex flex-col">
								<LabelWithDescription
									customClassName="mt-2"
									label="Auto Hangup the call after interaction?"
									description="Enable this option to automatically end the call when the interaction is clearly over. This helps avoid unnecessary call duration and reduces additional costs."
									showDescriptionDirectly={true}
								/>
								{!debrand && (
									<div className="mt-4">
										<LearnMore tutorialId={TUTORIALS.AUTO_HANGUP} debrand={debrand} />
									</div>
								)}
							</div>
						}
						name="autoHangupAfterInteraction"
						valuePropName="checked"
						className={`${baseClass}__input-item`}
					>
						<Switch />
					</Form.Item>
					<Form.Item
						noStyle
						shouldUpdate={(prevValues, currentValues) =>
							prevValues.autoHangupAfterInteraction !== currentValues.autoHangupAfterInteraction
						}
					>
						{({ getFieldValue }) =>
							getFieldValue('autoHangupAfterInteraction') && (
								<Form.Item
									label={
										<LabelWithDescription
											customClassName="mt-2"
											label="Text Message"
											description="This will be spoken while auto hanging up the call after interaction is over"
											showDescriptionDirectly={true}
										/>
									}
									name="autoHangupAfterInteractionText"
									className={`${baseClass}__input-item`}
								>
									<Input.TextArea rows={2} placeholder="Please enter text message" />
								</Form.Item>
							)
						}
					</Form.Item>
				</div>
			) : (
				''
			)}

			{(isIdleMessagesForInactivityEnabled && activeField === 'autoHangupIdleMessagesWorkflow') ||
			activeField === null ? (
				<div>
					<Alert
						className="my-4"
						type="info"
						closable={false}
						message={
							<div className="flex flex-col gap-1">
								<b>Please Note:</b>

								<p className="text-justify m-0">
									You can use this feature when the caller goes silent. Have the AI prompt them to
									continue the conversation.
								</p>
								<p className="text-justify m-0">
									Your message should be short and concise, like "Sorry, I didn't hear anything. Are
									you still there?", "Since I didn’t get a response, I’ll go ahead and hang up.
									Thank you, and have a great day".
								</p>
							</div>
						}
					/>
					<Form.Item
						noStyle
						shouldUpdate={(prevValues, currentValues) =>
							prevValues.idleMessage !== currentValues.idleMessage
						}
					>
						<Form.Item
							initialValue={formInitialValues?.idleMessage}
							label={
								<LabelWithDescription
									customClassName="mt-2"
									label="Idle Message"
									description="AI assistant will say this message when the user is inactive"
									showDescriptionDirectly={true}
								/>
							}
							name="idleMessage"
							className={`${baseClass}__input-item`}
						>
							<Input.TextArea
								rows={1}
								value={form.getFieldValue('idleMessage')}
								onChange={(event) =>
									form.setFieldsValue({ idleMessage: event?.currentTarget.value })
								}
								placeholder={"Sorry, I didn't hear anything. Are you still there?"}
							/>
						</Form.Item>
					</Form.Item>
					<Form.Item
						noStyle
						shouldUpdate={(prevValues, currentValues) =>
							prevValues.autoHangupIdleMessage !== currentValues.autoHangupIdleMessage
						}
					>
						<Form.Item
							initialValue={formInitialValues?.autoHangupIdleMessage}
							label={
								<LabelWithDescription
									customClassName="mt-2"
									label="Idle Hangup Message"
									description="AI assistant will say this message before ending the call due to inactivity."
									showDescriptionDirectly={true}
								/>
							}
							name="autoHangupIdleMessage"
							className={`${baseClass}__input-item`}
						>
							<Input.TextArea
								rows={1}
								value={form.getFieldValue('autoHangupIdleMessage')}
								onChange={(event) =>
									form.setFieldsValue({ autoHangupIdleMessage: event?.currentTarget.value })
								}
								placeholder={
									'Since I didn’t get a response, I’ll go ahead and hang up. Thank you, and have a great day'
								}
							/>
						</Form.Item>
					</Form.Item>
				</div>
			) : (
				''
			)}

			{activeField === 'autoHangupOnSilenceWorkflow' || activeField === null ? (
				<Form.Item
					className={`${baseClass}__input-item`}
					name="autoHangupOnSilence"
					label={
						<div className={`${baseClass}__item`}>
							<LabelWithDescription
								customClassName="w-8/12"
								label="Auto Hangup after silence"
								description="Maximum silence duration (in minutes) of the AI receptionist before it automatically hang up"
								icon={<AccessAlarmsOutlined className="mr-1 mb-1" />}
								showDescriptionDirectly={true}
							/>
							{!debrand && (
								<div className="mt-4">
									<LearnMore tutorialId={TUTORIALS.MAX_CALL_DURATION} debrand={debrand} />
								</div>
							)}
						</div>
					}
				>
					<Slider
						min={1}
						max={3}
						className="mx-4"
						value={form.getFieldValue('autoHangupOnSilence')}
						onChange={handleMaxSilenceDuration}
						marks={{ 1: '1', 2: '2', 3: '3' }}
					/>
				</Form.Item>
			) : (
				''
			)}

			{activeField === 'blockedNumbers' || activeField === null ? (
				<Form.Item
					className={
						activeField === 'blockedNumbers'
							? `${baseClass}_active-class pt-4`
							: `${baseClass}__item__with-tbl`
					}
				>
					<div className="flex-1 mb-3">
						<LabelWithDescription
							customClassName="w-full"
							label="Blocked Numbers"
							description="List of blocked numbers that are restricted from calling your receptionist."
							icon={<Block className="mr-1 mb-1" />}
							showDescriptionDirectly={true}
						/>
						{formInitialValues.blockedNumbers && formInitialValues.blockedNumbers.length > 0 ? (
							<div className="mt-4 max-h-[50vh] overflow-y-auto overflow-x-hidden">
								<Row gutter={[16, 16]} className="py-4 px-1">
									{formInitialValues.blockedNumbers.map((item: string, index: number) => (
										<Col key={index} span={8}>
											<div className="pl-4 py-1 rounded-md text-center shadow-md border cursor-default">
												<CopyableText text={item} />
											</div>
										</Col>
									))}
								</Row>
							</div>
						) : (
							<Empty />
						)}
					</div>
					<div className="flex justify-end">
						<Button type="primary" onClick={handleBlockedNumbersClick}>
							Edit
						</Button>
					</div>
					<CustomModal
						title={<strong>Blocked Numbers</strong>}
						description="List of blocked numbers that are restricted from calling your receptionist."
						children={
							<BlockedNumbersForm
								refetch={refetch}
								blockedNumbers={formInitialValues.blockedNumbers || []}
							/>
						}
						modalId={MODAL_IDS.BLOCKED_NUMBERS_MODAL}
						confirmAction={handleConfirm}
						footer={null}
						width={450}
					/>
				</Form.Item>
			) : (
				''
			)}

			{activeField === 'promptForLandline' || activeField === null ? (
				<Form.Item
					label={
						<LabelWithDescription
							customClassName="mt-2"
							label="Landline Number SMS Prompt"
							description="SMS cannot be delivered to landline numbers. If a user calls from a landline, ensure an alternative method of communication is available."
							icon={<SmsFailedOutlined className="mr-1 mb-1" />}
							showDescriptionDirectly={true}
						/>
					}
					name="promptForLandline"
					className={`${baseClass}__input-item`}
				>
					<Input.TextArea
						rows={2}
						placeholder="we can not send sms as reciever's number is a landline number"
					/>
				</Form.Item>
			) : (
				''
			)}

			{activeField === 'roboCallNumbers' || activeField === null ? (
				<Form.Item
					className={
						activeField === 'roboCallNumbers'
							? `${baseClass}_active-class pt-4`
							: `${baseClass}__item__with-tbl`
					}
				>
					<div className="flex-1 mb-3">
						<div className="flex items-start justify-between mb-6">
							<LabelWithDescription
								customClassName="w-full"
								label="Enable Robo Call Detection?"
								description="Enable robocall detection to block spam calls from reaching your receptionist, reducing unnecessary charges and keeping your billing efficient."
								icon={<RobotOutlined size={20} className="mr-1 mb-1" />}
								showDescriptionDirectly={true}
							/>
							<Form.Item name="enabledRoboCallDetection" valuePropName="checked">
								<Switch />
							</Form.Item>
						</div>

						<LabelWithDescription
							customClassName="w-full pl-6"
							label="Robo Call Numbers"
							description="Numbers identified as robocalls and blocked numbers, can be configured to whitelist specific numbers."
							showDescriptionDirectly={true}
						/>
						{formInitialValues?.roboCallBlocklistNumbers?.length === 0 &&
							formInitialValues?.roboCallWhitelistNumbers?.length === 0 && <Empty />}
						{formInitialValues.roboCallBlocklistNumbers &&
							formInitialValues.roboCallBlocklistNumbers.length > 0 && (
								<div className="mt-4 pl-6">
									<span className="font-semibold">Numbers in Blocklist</span>
									<div className="max-h-[28vh] overflow-y-auto overflow-x-hidden">
										<Row gutter={[16, 16]} className="py-4 px-1">
											{formInitialValues.roboCallBlocklistNumbers.map(
												(item: string, index: number) => (
													<Col key={index} span={8}>
														<div className="relative flex items-center gap-2 pl-4 py-1 rounded-md text-center shadow-md border cursor-default">
															<CopyableText text={item} />
															<Tooltip title="Click to Move to Whitelist">
																<div
																	className="absolute top-[-16px] right-[-16px] flex justify-center p-1 rounded-full bg-blue-100 hover:bg-blue-300"
																	onClick={() => moveToRobocallWhitelist(item)}
																>
																	<FollowTheSignsOutlined
																		fontSize="small"
																		className="cursor-pointer text-red-600"
																	/>
																</div>
															</Tooltip>
														</div>
													</Col>
												),
											)}
										</Row>
									</div>
								</div>
							)}
						{formInitialValues.roboCallWhitelistNumbers &&
							formInitialValues.roboCallWhitelistNumbers.length > 0 && (
								<div className="mt-4 pl-6">
									<span className="font-semibold">Numbers in Whitelist</span>
									<div className="max-h-[28vh] overflow-y-auto overflow-x-hidden">
										<Row gutter={[16, 16]} className="py-4 px-1">
											{formInitialValues.roboCallWhitelistNumbers.map(
												(item: string, index: number) => (
													<Col key={index} span={8}>
														<div className="relative flex items-center gap-2 pl-4 py-1 rounded-md text-center shadow-md border cursor-default">
															<CopyableText text={item} />
															<Tooltip title="Click to Move to Blocklist">
																<div
																	className="absolute top-[-16px] right-[-16px] flex justify-center p-1 rounded-full bg-blue-100 hover:bg-blue-300"
																	onClick={() => moveToRobocallBlocklist(item)}
																>
																	<NaturePeopleOutlined
																		fontSize="small"
																		className="cursor-pointer text-green-600"
																	/>
																</div>
															</Tooltip>
														</div>
													</Col>
												),
											)}
										</Row>
									</div>
								</div>
							)}
					</div>
					<CustomModal
						title={<strong>Blocked Numbers</strong>}
						description="List of blocked numbers that are restricted from calling your receptionist."
						children={
							<BlockedNumbersForm
								refetch={refetch}
								blockedNumbers={formInitialValues.blockedNumbers || []}
							/>
						}
						modalId={MODAL_IDS.BLOCKED_NUMBERS_MODAL}
						confirmAction={handleConfirm}
						footer={null}
						width={450}
					/>
				</Form.Item>
			) : (
				''
			)}

			{activeField === 'roboSMSNumbers' || activeField === null ? (
				<Form.Item
					className={
						activeField === 'roboSMSNumbers'
							? `${baseClass}_active-class pt-4`
							: `${baseClass}__item__with-tbl`
					}
				>
					<div className="flex-1 mb-3">
						<LabelWithDescription
							customClassName="w-full"
							label="SMS Bot Numbers"
							description="List of numbers detected as SMS bots and blocked from SMS interactions."
							icon={<Block className="mr-1 mb-1" />}
							showDescriptionDirectly={true}
						/>
						<div className="mt-4 font-semibold">Blocked</div>
						{roboSMSBlocklistNumbersCurrent ? (
							<div className="mb-4 max-h-[50vh] overflow-y-auto overflow-x-hidden">
								<Row gutter={[16, 16]} className="py-4 px-1">
									{roboSMSBlocklistNumbersCurrent.map((item, index: number) => (
										<Col key={index} span={8}>
											<div className="relative flex items-center gap-2 pl-4 py-1 rounded-md text-center shadow-md border cursor-default text-red-500">
												<CopyableText text={item.number} />
												<Tooltip title="Remove from blacklist">
													<div
														className="absolute right-[-12px] flex justify-center p-1 rounded-full bg-blue-100 hover:bg-blue-300"
														onClick={() => removeRoboSMSBlacklist(item.number)}
													>
														<DeleteOutlined className="cursor-pointer text-red-600 text-sm" />
													</div>
												</Tooltip>
											</div>
										</Col>
									))}
								</Row>
							</div>
						) : (
							<Empty />
						)}

						<div className="font-semibold">Previously Blocked</div>
						{roboSMSBlocklistNumbersPrevious ? (
							<div className="max-h-[50vh] overflow-y-auto overflow-x-hidden">
								<Row gutter={[16, 16]} className="py-4 px-1">
									{roboSMSBlocklistNumbersPrevious.map((item, index: number) => (
										<Col key={index} span={8}>
											<div className="relative flex items-center gap-2 pl-4 py-1 rounded-md text-center shadow-md border cursor-default text-green-500">
												<CopyableText text={item.number} />
												<Tooltip title="Remove from blacklist">
													<div
														className="absolute right-[-12px] flex justify-center p-1 rounded-full bg-blue-100 hover:bg-blue-300"
														onClick={() => removeRoboSMSBlacklist(item.number)}
													>
														<DeleteOutlined className="cursor-pointer text-red-600 text-sm" />
													</div>
												</Tooltip>
											</div>
										</Col>
									))}
								</Row>
							</div>
						) : (
							<Empty />
						)}
					</div>
				</Form.Item>
			) : (
				''
			)}
		</>
	);

	const baseClass = 'tweak-advanced-settings-form';

	return (
		<div className={baseClass}>
			<Spin
				spinning={mutation.isPending}
				indicator={<LoadingOutlined style={{ fontSize: '48px' }} spin />}
				percent="auto"
			>
				<OverViewCardForm
					form={form}
					initialValues={formInitialValues}
					formItems={<FormItems />}
					handleSubmit={handleSubmit}
					isFieldTouchReset={isFieldTouchReset}
				/>
			</Spin>
		</div>
	);
};

export default TweakAdvancedSettingsForm;
